import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import { useTranslation } from "react-i18next";
import { TranslatedPropContentDTO } from "../../dto/TranslatedPropContentDTO";
import { TranslatedPropDTO } from "../../dto/TranslatedPropDTO";
import { PropCategoryChips } from "./PropCategoryChip";

interface PropLibraryDetailsPaneProps {
  prop?: TranslatedPropDTO;
  /** Loaded lazily after selection; interactions appear when it arrives. */
  content: TranslatedPropContentDTO | null;
}

/**
 * The prop library's details column on the shared 400px pattern: name,
 * preview card, categories, and the Available Interactions chips — which wrap,
 * per the Phase 3 check. Adding happens in the footer, not here.
 */
export default function PropLibraryDetailsPane(
  props: PropLibraryDetailsPaneProps
) {
  const { prop, content } = props;
  const { t } = useTranslation();

  const paneClass =
    "w-[400px] shrink-0 border-l border-creator-border bg-creator-primary-background p-[16px] flex flex-col overflow-y-auto pit-quiet-scrollbar";

  if (!prop) return <div className={paneClass} />;

  const sectionLabel = (label: string) => (
    <div className="mt-[14px] mb-[6px] text-[10px] font-semibold uppercase tracking-[0.08em] text-creator-text-sub">
      {label}
    </div>
  );

  return (
    <div className={paneClass}>
      <div className="text-[15px] font-semibold text-creator-text">
        {prop.name}
      </div>

      <div className="mt-[12px] h-[230px] shrink-0 rounded-[8px] border border-creator-border bg-creator-surface flex items-center justify-center overflow-hidden">
        {prop.downloadUri ? (
          <img
            src={prop.downloadUri}
            alt=""
            className="object-contain max-w-full max-h-full p-[16px]"
          />
        ) : (
          <span className="text-[56px] text-creator-text-faded flex">
            <ViewInArOutlinedIcon fontSize="inherit" color="inherit" />
          </span>
        )}
      </div>

      {prop.categories.length > 0 && (
        <>
          {sectionLabel(t("prop_library_ui.categories_label"))}
          <div className="flex flex-wrap gap-[6px]">
            <PropCategoryChips categoryIds={prop.categories} max={99} />
          </div>
        </>
      )}

      {content && content.id === prop.id && content.interactions.length > 0 && (
        <>
          {sectionLabel(t("prop_library_ui.available_interactions"))}
          <div className="flex flex-wrap gap-[6px]">
            {content.interactions.map((interaction) => (
              <span
                key={interaction.id}
                className="px-[8px] py-[3px] rounded-full border border-creator-border text-[10px] leading-[14px] text-creator-text-sub whitespace-nowrap"
              >
                {interaction.name}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
